import { type Rational, rat, isTerminating } from '../math/rational.ts';

/**
 * Parse a player's typed/handwritten answer into an exact Rational.
 * Accepts: "7", "-7", "−7", "+7", "3/4", "-3/4", "3 / 4", "0.75", "0,75", ".5", "-0.5", "1 1/2" is NOT accepted.
 * Returns null for anything else (empty, "--3", "3/0", "1.2.3", letters).
 */
export function parseAnswer(input: string): Rational | null {
  const s = input
    .normalize('NFKC')
    .replace(/[−–—‒]/g, '-')
    .replace(/,/g, '.')
    .replace(/\s+/g, '');
  if (!s) return null;

  const frac = /^([+-]?)(\d+)\/(\d+)$/.exec(s);
  if (frac) {
    const d = Number(frac[3]);
    if (d === 0) return null;
    const n = Number(frac[2]) * (frac[1] === '-' ? -1 : 1);
    return safe(n, d);
  }

  const dec = /^([+-]?)(\d*)(?:\.(\d+))?$/.exec(s);
  if (dec && (dec[2] || dec[3])) {
    const whole = dec[2] || '0';
    const fracPart = dec[3] ?? '';
    if (fracPart.length > 6) return null;
    const d = 10 ** fracPart.length;
    const n = (Number(whole) * d + Number(fracPart || '0')) * (dec[1] === '-' ? -1 : 1);
    return safe(n, d);
  }
  return null;
}

function safe(n: number, d: number): Rational | null {
  if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d)) return null;
  return rat(n, d);
}

export type AnswerKind = 'integer' | 'fraction' | 'decimal';

/**
 * Display a value the way the problem expects it to be written.
 * fraction → "3/4" (or "-3/4"), decimal → "0.75" (locale 'af' uses a decimal comma), integer → "7".
 * Minus is always U+2212 for display.
 */
export function formatRational(v: Rational, kind: AnswerKind = 'integer', locale: 'en' | 'af' = 'en'): string {
  const sign = v.n < 0 ? '−' : '';
  const n = Math.abs(v.n);
  if (v.d === 1) return `${sign}${n}`;
  if (kind === 'decimal' && isTerminating(v)) {
    const s = (n / v.d).toString();
    return sign + (locale === 'af' ? s.replace('.', ',') : s);
  }
  return `${sign}${n}/${v.d}`;
}
